/* Purpose: Clients can view and edit personal data. Clients can upload/delete/preview a ValidID document.
 * Props: credentials (map)
 *        stopSave (boolean)
 *        clickedSave (boolean)
 *        handleClickSave (func)
 *        setRequPersonalInfo (setState)
 *        requPersonalInfo (list of maps)
 *        previewFileCache (list)
 *        setPreviewFileCache (setState)
 *        dbPersonalInfo (map)
 *        setDBPersonalInfo (setState)
 *        onBlur(func)
 *        saveBtnText(str)
 *        handleChangeTab (func)
 *        handleInfoChange(func)
 *        isUploadingFile (boolean)
 *        setIsSavingFile (setState)
 * Author: R. Markowitz (April 2025)
 * Dev Notes: 
*/

import React, {useEffect} from 'react';
import Form from 'react-bootstrap/Form';
import SubmitButton from '../../Components/Button';
import { Container, Row, Col, Alert, Image, Stack } from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import { useNavigate } from 'react-router-dom';

const Personal = ({credentials, stopSave, clickedSave, handleClickSave, setRequPersonalInfo, requPersonalInfo, previewFileCache, setPreviewFileCache, dbPersonalInfo, setDBPersonalInfo, onBlur, saveBtnText, handleChangeTab, handleInfoChange, isUploadingFile, setIsSavingFile}) => {
    const viewIcon = require("../../Images/viewSmall.png")
    const deleteIcon = require("../../Images/deleteSmall.png")
    const navigateTo = useNavigate();

    useEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: 'smooth' });
    }, [])

    // When remove is clicked, delete the file from s3, and form, set isSavingFile to true to trigger saving the changes to dynamodb
    const handleRemove = async (index) => {        
        const updatedRequPersonalInfo = [...requPersonalInfo]
        const filePath = updatedRequPersonalInfo[index].value;

        try {
            const response = await fetch('https://6gps7s68d9.execute-api.us-east-1.amazonaws.com/deleteFileInS3', {
                "method":"POST",
                "body":JSON.stringify({
                    "filePath": filePath,
                    "credentials": credentials
                })
            })

            let data = "";
            let isRemoved = false;

            if (response.ok){
                data = await response.json();
                if (data == "Successfully deleted file from S3!"){
                    isRemoved = true;
                }
                
            }

            if (isRemoved) {
                console.log("File successfully removed!")

                updatedRequPersonalInfo[index] = {
                ...updatedRequPersonalInfo[index], file: null, value: ""
                };

                // Reset the file path in dynamodb
                const updateFieldName = updatedRequPersonalInfo[index].fieldName
                setDBPersonalInfo({...dbPersonalInfo, 
                [updateFieldName] : ""
                    })
                setIsSavingFile(true);

                // Remove the file from file cache
                const cacheIndex =  previewFileCache.findIndex(file => file.name === filePath);

                // Check if the index is valid (i.e., the object exists in the array)
                if (cacheIndex !== -1) {
                    // Remove the object from the array
                    previewFileCache.splice(index, 1);
                }
            } else {
                alert('Failed to remove file from S3.');
            }
        }catch (error){
            console.error(error)
            alert("Something went wrong while trying to delete the file, ", error);
            
        }

        setRequPersonalInfo(updatedRequPersonalInfo);
    };

    // View an uploaded file in a seperate tab using a presigned url
    const handleClickedPreview = async (index) => {
        const fetchFileFromS3 = async (urlData, fileName) => {
            try{     
            const response = await fetch(urlData, {responseType: "arrayBuffer"});
            
            if (response.ok){
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                // cache the file
                setPreviewFileCache([...previewFileCache, {url, name: fileName}]);
    
    
            }else{
                console.error("Something went wrong when fetching the file. Response not ok!")
                alert("Something went wrong when trying to view the file.")
            }
    
            }catch (error){
                console.error("Something went wrong when trying to view the file. ", error)
                alert("Something went wrong when trying to view the file.")
            }
        }


        const filePath = requPersonalInfo[index].value;
        const cacheIndex =  previewFileCache.findIndex(f => f.name === filePath);
        
        // If the file is cached, open it from fileCache.
        // If not, call handleClickedPreview to fetch from s3
        if (cacheIndex !== -1){
        console.log("Opening file from cache...");          
        let url  = previewFileCache[cacheIndex].url
        console.log("Cached url: ", url)
        window.open(url, '_blank');
        }else{

            try{
                const urlResponse = await fetch('https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/downloadFileFromS3', {
                    "method": "POST",
                    "body": JSON.stringify({
                        "filePath":filePath,
                        "credentials": credentials
                    })
                });
                if (urlResponse.ok){
                const presignedData = await urlResponse.json();
                const fetchedFileResponse = await fetchFileFromS3(presignedData, filePath);
    
                }else{
                console.log("Something went wrong when trying to get a presigned url. response not ok.")
                alert("Something went wrong when trying to view the file.")
                }
            }catch (error){
                console.error("Something went wrong when getting or using the presigned url to view the file. Error: ", error)
                alert("Something went wrong when trying to view the file.")
            }   
        }
    };

    const handleBack = async() => {
        if (!clickedSave){
            alert("Your information has not been saved! Please save before leaving this page.")
        }else{
            navigateTo("/Portal")
        }
    }




    return(
        <div>
            <Container >
                <Form className='my-3'>
                    {requPersonalInfo.map((item, index) => (
                        <Row className='my-1' key = {index}>
                            {item.fieldType === "file"?(
                                <Row className='mt-3'>
                                    <Col>
                                    <Form.Label>{item.fieldName}</Form.Label>
                                    {item.toolTip &&(
                                        <Form.Text id = {item.fileName} muted>  {item.toolTip}</Form.Text>
                                    )}
                                    </Col>
                                    <Col sm={1}>
                                    {isUploadingFile && (
                                        <Spinner
                                            as="span"
                                            animation='border'
                                            role='status'
                                            aria-hidden = 'true'
                                        >
                                            <span className='visually-hidden'>Loading...</span>
                                        </Spinner>
                                        
                                    )}
                                    </Col>
                                    <Col>
                                    {item.value === ""? (
                                        <Container className = "m-2">
                                        <Row>    
                                        <Col>
                                            <Form.Control 
                                                type="file"
                                                size="sm" 
                                                style={{ backgroundColor: item.value == ""? '#b85c5c' : 'transparent' }} 
                                                onChange={(event) => handleInfoChange(index, event)} 
                                            />
                                        </Col>
                                        </Row>
                                        </Container>
                                    ) : (
                                        <Container>
                                        <Stack direction = "horizontal" gap={3}>
                                                <Form.Select className = "ms-auto" value = {item.value}>
                                                    <option disabled>Uploaded File</option>
                                                    <option value = {item.value}>{item.value.split("/")[1]}</option>
                                                </Form.Select>
                                                <a onClick = {() => {handleRemove(index)}}><Image alt = "Delete" src= {deleteIcon} fluid/></a>
                                                <a onClick = {() => {handleClickedPreview(index)}}><Image alt = "View" src= {viewIcon} fluid/></a>                                  
                                        </Stack>
                                        </Container>

                                    )}
                                </Col>
                                </Row>
                            ):(
                                <Form.Group key = {index} type="text"> 
                                    <Form.Label>{item.fieldName}: </Form.Label>
                                    {item.toolTip &&(
                                        <Form.Text className = 'ms-1' id = {item.fieldName} muted>{item.toolTip}</Form.Text>
                                    )}
                                    <Form.Control 
                                        id={item.fieldName} 
                                        value= {item.value} 
                                        onChange={(event) => {handleInfoChange(index, event)}}
                                        isInvalid = {"isValid" in item?!item.isValid:false}
                                        onBlur={() => onBlur(item, index)}
                                    />
                                    
                                    <Form.Control.Feedback type = "invalid">{item.invalidMsg}</Form.Control.Feedback>
                                </Form.Group>
                            )}
                            
                        </Row>
                    ))}

                    {stopSave && (
                        <Row className="align-items-center mt-3">
                        <Alert className = "text-center w-80" variant = "danger" >Your changes have NOT been saved. One or more fields contain invalid data. Please fix your data and try saving again.</Alert>
                        </Row>
                    )}

                    <Row  xs = {2} sm={2} md = {3} className='g-4 m-2'>
                        <Col className='text-start' xs={{order: 2}} md = {{order: 1}}>
                            <SubmitButton text = "<< Back" width = "52" onClick = {handleBack}/>
                        </Col>
                        <Col className='text-center' xs = {{span: '12', order: 1}} sm ={{span: '12', order: 1}} md = {{order: 2}}>
                            <SubmitButton text = {saveBtnText} onClick = {handleClickSave} width = "90"/>
                        </Col>
                        <Col className='text-end' xs = {{order: 3}} md = {{order: 3}}>
                            <SubmitButton text = "Next >>" width = "52" onClick = {() => handleChangeTab("Financial")}/>
                        </Col>
                    </Row>

                </Form>
            </Container>   

        </div>
        
    );

};

export default Personal;